/**
 * Single source of truth for the `POST /api/refresh` request.
 *
 * EVERY session refresher funnels through here — the router `beforeLoad`
 * bootstrap, the AuthProvider silent refresh on mount, the 60s proactive poll,
 * and the urql/ws auth exchanges — so that concurrent refreshes (which always
 * happen on a page reload, when both the router guard and AuthProvider restore
 * the session at once) collapse into ONE network request.
 *
 * This matters because the backend rotates the refresh-token cookie on every
 * call: two concurrent `/api/refresh` requests race, the loser reuses the
 * already-consumed cookie and gets a 401, and a perfectly valid session gets
 * bounced to /login (or left unauthenticated on the page) after a reload.
 *
 * Kept router-free (only depends on the dependency-free `token.store`) so
 * both `auth.ts` and the router-facing `auth-route.ts` can share it without
 * any import cycle.
 */
import { isImpersonating, isLoggedOut } from '@shared/auth/token.store';

let inFlight: Promise<Response> | null = null;

export function requestRefresh(): Promise<Response> {
  // The user explicitly logged out on this (or another) tab — refuse to
  // silently re-authenticate off the httpOnly refresh cookie until a fresh
  // login clears the flag. Short-circuits before hitting the network so a
  // logged-out tab can never resurrect a session.
  if (isLoggedOut()) {
    return Promise.resolve(new Response(null, { status: 401 }));
  }
  if (isImpersonating()) {
    return Promise.resolve(new Response(null, { status: 403 }));
  }
  if (!inFlight) {
    inFlight = fetch('/api/refresh', {
      method: 'POST',
      credentials: 'include',
    }).finally(() => {
      inFlight = null;
    });
  }
  // Clone so each concurrent caller gets its own independently-readable body.
  return inFlight.then((res) => res.clone());
}
